import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  SiMicrosoftazure, 
  SiTensorflow, 
  SiDatabricks, 
  SiCoursera, 
  SiUdemy, 
  SiDatacamp,
  SiGooglecloud,
  SiAnthropic
} from 'react-icons/si';
import { 
  FaCertificate, 
  FaSearch, 
  FaAward, 
  FaBriefcase, 
  FaShieldAlt, 
  FaBook,
  FaStar,
  FaPlus,
  FaHeartbeat,
  FaBalanceScale,
  FaRobot,
  FaCogs,
  FaGraduationCap
} from 'react-icons/fa';
import { portfolioData } from '../data/portfolioData';
import './Certifications.css';

// Provider icons mapping
const getProviderIcon = (provider: string) => {
  switch(provider.toLowerCase()) {
    case 'microsoft': return <SiMicrosoftazure />;
    case 'google': return <SiGooglecloud />;
    case 'databricks': return <SiDatabricks />;
    case 'coursera': return <SiCoursera />;
    case 'udemy': return <SiUdemy />;
    case 'datacamp': return <SiDatacamp />;
    case 'tensorflow': return <SiTensorflow />;
    case 'anthropic': return <SiAnthropic />;
    case 'deeplearning.ai': return <FaGraduationCap />;
    case 'hl7': return <FaHeartbeat />;
    case 'mlops foundation': return <FaCogs />;
    case 'ai ethics board': return <FaBalanceScale />;
    default: return <FaCertificate />;
  }
};

// Type icons mapping
const getTypeIcon = (type: string) => {
  switch(type.toLowerCase()) {
    case 'professional': return <FaBriefcase />;
    case 'technical': return <FaAward />;
    case 'course': return <FaBook />;
    case 'safety': return <FaShieldAlt />;
    case 'healthcare': return <FaHeartbeat />;
    case 'ethics': return <FaBalanceScale />;
    case 'ai': return <FaRobot />;
    default: return <FaCertificate />;
  }
};

const Certifications: React.FC = () => {
  const { certifications } = portfolioData;
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [hoveredCert, setHoveredCert] = useState<number | null>(null);

  // Filter certifications based on search and type
  const filteredCertifications = certifications.filter(cert => {
    const matchesSearch = cert.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         cert.provider.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = selectedType ? cert.type === selectedType : true;
    return matchesSearch && matchesType;
  });

  // Get all unique certification types
  const certTypes = Array.from(new Set(certifications.map(cert => cert.type)));

  // Match percentage calculation (for Netflix-style match)
  const getMatchPercentage = (importance: number) => {
    return Math.floor(importance);
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1,
      transition: {
        duration: 0.4
      }
    }
  };

  return (
    <div className="certifications-container">
      <div className="netflix-gradient-overlay">
        <div className="certifications-header">
          <h1 className="certifications-title">Professional Development</h1>
          <p className="certifications-subtitle">Certifications & Credentials</p>
        </div>

        <div className="search-and-filter">
          <div className="search-box">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search certifications..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
          
          <div className="filter-buttons">
            <button 
              className={`filter-button ${selectedType === null ? 'active' : ''}`}
              onClick={() => setSelectedType(null)}
            >
              All
            </button>
            {certTypes.map(type => (
              <button
                key={type}
                className={`filter-button ${selectedType === type ? 'active' : ''}`}
                onClick={() => setSelectedType(type)}
              >
                {type.charAt(0).toUpperCase() + type.slice(1)}
                {getTypeIcon(type)}
              </button>
            ))}
          </div>
        </div>

        <motion.div 
          className="certifications-grid"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {filteredCertifications.map((cert, index) => (
            <motion.div 
              key={index}
              className="certification-card"
              variants={itemVariants}
              onMouseEnter={() => setHoveredCert(index)}
              onMouseLeave={() => setHoveredCert(null)}
            >
              <div className="certification-image">
                <div className="provider-icon">
                  {getProviderIcon(cert.provider)}
                </div>
                {hoveredCert === index && (
                  <div className="hover-overlay">
                    <a 
                      href={cert.credentialUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="view-button"
                    >
                      <FaPlus /> My List
                    </a>
                  </div>
                )}
              </div>
              
              <div className="certification-content">
                <div className="certification-match">
                  <span className="match-percentage">{getMatchPercentage(cert.importance)}% Match</span>
                  <span className="certification-date">{cert.date}</span>
                </div>
                
                <h3 className="certification-title">{cert.title}</h3>
                
                <div className="certification-meta">
                  <span className="certification-provider">
                    {getProviderIcon(cert.provider)}
                    {cert.provider}
                  </span>
                  
                  <span className="certification-type">
                    {getTypeIcon(cert.type)}
                    {cert.type.charAt(0).toUpperCase() + cert.type.slice(1)}
                  </span>
                </div>
                
                <div className="certification-rating">
                  {[...Array(5)].map((_, i) => (
                    <FaStar 
                      key={i} 
                      className={`star ${i < Math.round(cert.importance / 20) ? 'filled' : ''}`} 
                    />
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
      
      <div className="similar-certifications">
        <h2>Because you're interested in AI Innovation</h2>
        <div className="suggestion-row">
          {certifications
            .filter(cert => cert.date >= "2024" && (cert.type === 'technical' || cert.type === 'ethics'))
            .slice(0, 5)
            .map((cert, index) => (
              <div key={index} className="suggestion-item">
                <div className="suggestion-icon">{getProviderIcon(cert.provider)}</div>
                <span>{cert.title}</span>
              </div>
            ))}
        </div>
      </div>

      <div className="similar-certifications">
        <h2>Popular in Professional Development</h2>
        <div className="suggestion-row">
          {certifications
            .filter(cert => cert.type === 'professional' || cert.type === 'healthcare')
            .slice(0, 4)
            .map((cert, index) => (
              <div key={index} className="suggestion-item">
                <div className="suggestion-icon">{getTypeIcon(cert.type)}</div>
                <span>{cert.title}</span>
              </div>
            ))}
        </div>
      </div>

      <div className="netflix-originals">
        <h2>AI Certifications <span className="originals-badge">New 2025</span></h2>
        <div className="suggestion-row">
          {certifications
            .filter(cert => cert.date === "2025")
            .slice(0, 4)
            .map((cert, index) => (
              <div key={index} className="suggestion-item original-item">
                <div className="netflix-n">N</div>
                <span>{cert.title}</span>
                <div className="hot-badge">HOT</div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};

export default Certifications;